'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { BodyPortal } from '../BodyPortal'
import './Button.css'
import './DropdownButton.css'

export type DropdownButtonVariant = 'default' | 'primary' | 'secondary' | 'outline' | 'ghost'
export type DropdownButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export interface DropdownGroup {
  label?: string
  options: DropdownOption[]
}

export interface DropdownOption {
  value: string
  label: string
  icon?: string
  disabled?: boolean
  danger?: boolean
  dot?: boolean       // show a coloured indicator dot (e.g. "has saved template")
  selected?: boolean  // highlight as currently active item
}

export interface DropdownButtonProps {
  icon?: string
  label?: string
  variant?: DropdownButtonVariant
  size?: DropdownButtonSize
  groups: DropdownGroup[]
  align?: 'left' | 'right' | 'auto'
  disabled?: boolean
  className?: string
  menuClassName?: string
  maxMenuHeight?: number
  onSelect?: (value: string) => void
}

interface MenuPosition {
  top?: number
  bottom?: number
  left?: number
  right?: number
  maxHeight?: number
}

export const DropdownButton: React.FC<DropdownButtonProps> = ({
  icon,
  label,
  variant = 'default',
  size = 'md',
  groups,
  align = 'auto',
  disabled = false,
  className = '',
  menuClassName = '',
  maxMenuHeight,
  onSelect
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null)

  // Calculate menu position based on viewport
  const calculatePosition = useCallback(() => {
    if (!containerRef.current || !menuEl) return

    const triggerRect = containerRef.current.getBoundingClientRect()
    const menuRect = menuEl.getBoundingClientRect()
    const fullMenuHeight = Math.max(menuEl.scrollHeight, menuRect.height)
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const padding = 8

    const position: MenuPosition = {}

    // We render the menu in a portal (fixed positioning), so compute absolute viewport coordinates.
    // Vertical position: open down if it fits, otherwise up.
    const spaceBelow = viewportHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top

    const menuHeight = maxMenuHeight ? Math.min(fullMenuHeight, maxMenuHeight) : fullMenuHeight
    const openDown = spaceBelow >= menuHeight + padding || spaceBelow >= spaceAbove
    const desiredTop = openDown
      ? (triggerRect.bottom + 4)
      : (triggerRect.top - menuHeight - 4)

    // Clamp and compute maxHeight if it doesn't fully fit.
    if (openDown) {
      position.top = Math.min(desiredTop, viewportHeight - padding - 40)
      const available = viewportHeight - position.top - padding
      const cappedHeight = maxMenuHeight ? Math.min(maxMenuHeight, available) : available
      if (maxMenuHeight || cappedHeight < fullMenuHeight) position.maxHeight = Math.max(120, cappedHeight)
    } else {
      // If opening up, ensure top is not above viewport
      const clampedTop = Math.max(padding, desiredTop)
      position.top = clampedTop
      const available = triggerRect.top - 4 - padding
      const cappedHeight = maxMenuHeight ? Math.min(maxMenuHeight, available) : available
      if (maxMenuHeight || cappedHeight < fullMenuHeight) position.maxHeight = Math.max(120, cappedHeight)
    }

    // Horizontal position (viewport clamped)
    let desiredLeft = triggerRect.left
    if (align === 'right') {
      desiredLeft = triggerRect.right - menuRect.width
    } else if (align === 'auto') {
      // Prefer left align; if it overflows, try right align.
      const fitsLeft = (triggerRect.left + menuRect.width) <= (viewportWidth - padding)
      const fitsRight = (triggerRect.right - menuRect.width) >= padding
      if (!fitsLeft && fitsRight) desiredLeft = triggerRect.right - menuRect.width
    }

    // Clamp within viewport
    desiredLeft = Math.min(Math.max(desiredLeft, padding), viewportWidth - padding - menuRect.width)
    position.left = desiredLeft

    setMenuPosition(position)
  }, [align, maxMenuHeight, menuEl])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      const clickInsideTrigger = !!containerRef.current && containerRef.current.contains(target)
      const clickInsideMenu = !!menuRef.current && menuRef.current.contains(target)
      if (!clickInsideTrigger && !clickInsideMenu) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  // Calculate position when menu opens
  useEffect(() => {
    if (isOpen) {
      // Wait for menu to render
      requestAnimationFrame(() => {
        calculatePosition()
      })
    }
  }, [isOpen, calculatePosition])

  // When the portaled menu element is mounted, position it immediately.
  useEffect(() => {
    if (!isOpen || !menuEl) return
    requestAnimationFrame(() => {
      calculatePosition()
    })
  }, [isOpen, menuEl, calculatePosition])

  // Recalculate on resize/scroll
  useEffect(() => {
    if (!isOpen) return

    const handleReposition = (event?: Event) => {
      const target = event?.target as Node | null
      if (target && menuRef.current?.contains(target)) return
      calculatePosition()
    }
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [isOpen, calculatePosition])

  const handleSelect = (value: string) => {
    onSelect?.(value)
    setIsOpen(false)
  }

  const buttonClasses = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    'ui-dropdown-btn__trigger',
    isOpen && 'ui-dropdown-btn__trigger--open',
    className
  ].filter(Boolean).join(' ')

  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    top: menuPosition.top,
    left: menuPosition.left,
    maxHeight: menuPosition.maxHeight,
    overflowY: menuPosition.maxHeight ? 'auto' : undefined,
    zIndex: 10050,
    visibility: Object.keys(menuPosition).length > 0 ? 'visible' : 'hidden'
  }

  const setMenuRef = useCallback((el: HTMLDivElement | null) => {
    menuRef.current = el
    setMenuEl(el)
  }, [])

  return (
    <div ref={containerRef} className="ui-dropdown-btn">
      <button
        type="button"
        className={buttonClasses}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {icon && <i className={`${icon} ui-btn__icon`} />}
        {label && <span className="ui-btn__text">{label}</span>}
        <i 
          className={`fa-duotone fa-chevron-down ui-dropdown-btn__chevron ${isOpen ? 'ui-dropdown-btn__chevron--open' : ''}`} 
        />
      </button>

      {isOpen && (
        <BodyPortal>
          <div ref={setMenuRef} className={['ui-dropdown-btn__menu', menuClassName].filter(Boolean).join(' ')} style={menuStyle}>
            {groups.map((group, gIdx) => (
              <div key={gIdx} className="ui-dropdown-btn__group">
                {group.label && (
                  <div className="ui-dropdown-btn__group-label">
                    {group.label}
                  </div>
                )}
                {group.options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={[
                      'ui-dropdown-btn__item',
                      option.danger    ? 'ui-dropdown-btn__item--danger'   : '',
                      option.selected  ? 'ui-dropdown-btn__item--selected' : '',
                    ].filter(Boolean).join(' ')}
                    disabled={option.disabled}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.icon && <i className={`${option.icon} ui-dropdown-btn__item-icon`} />}
                    <span className="ui-dropdown-btn__item-label">{option.label}</span>
                    {option.dot && <span className="ui-dropdown-btn__item-dot" />}
                  </button>
                ))}
                {gIdx < groups.length - 1 && <div className="ui-dropdown-btn__divider" />}
              </div>
            ))}
          </div>
        </BodyPortal>
      )}
    </div>
  )
}

export default DropdownButton
